import type { FilterKey } from "../filter/filterDefinitions";
import type { GroupedSuggestionConfig } from "../filter/groupedSuggestion";
import { mockProsjekter } from "./mockProsjekter";
import { mapBackendRowToProsjektRow } from "./prosjektlisteBackendMapper";
import backendProsjekterRaw from "./prosjektlisten.json";
import type { BackendProsjektRow, ProsjektlisteDataSource, ProsjektRow } from "./types";

const prosjektlisteDataSourceParamKey = "prosjektlisteDataSource";

const backendProsjekter = (backendProsjekterRaw as BackendProsjektRow[]).map(mapBackendRowToProsjektRow);
const mockProsjekterMapped = (mockProsjekter as BackendProsjektRow[]).map(mapBackendRowToProsjektRow);

function slugifyValue(value: string): string {
    return value
        .toLocaleLowerCase("nb")
        .replace(/æ/g, "ae")
        .replace(/ø/g, "o")
        .replace(/å/g, "a")
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function sortConfigAlphabetically(config: GroupedSuggestionConfig): GroupedSuggestionConfig {
    return {
        ...config,
        groups: [...config.groups]
            .map((group) => ({
                ...group,
                children: [...group.children].sort((a, b) => a.label.localeCompare(b.label, "nb")),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, "nb")),
        standaloneOptions: config.standaloneOptions
            ? [...config.standaloneOptions].sort((a, b) => a.label.localeCompare(b.label, "nb"))
            : undefined,
    };
}

function buildBackendRegionConfig(rows: BackendProsjektRow[]): GroupedSuggestionConfig {
    const kommuneSetByFylke = new Map<string, Set<string>>();
    const standaloneMunicipalities = new Set<string>();

    rows.forEach((row) => {
        const fylke = row.prosjektfylke?.trim();
        const kommune = row.prosjektkommune?.trim();

        if (fylke && kommune) {
            const kommuner = kommuneSetByFylke.get(fylke) ?? new Set<string>();
            kommuner.add(kommune);
            kommuneSetByFylke.set(fylke, kommuner);
            return;
        }

        if (fylke && !kommune) {
            if (!kommuneSetByFylke.has(fylke)) {
                kommuneSetByFylke.set(fylke, new Set<string>());
            }
            return;
        }

        if (!fylke && kommune) {
            standaloneMunicipalities.add(kommune);
        }
    });

    const groups = Array.from(kommuneSetByFylke.entries()).map(([fylke, kommuner]) => ({
        label: fylke,
        value: `fylke-${slugifyValue(fylke)}`,
        children: Array.from(kommuner).map((kommune) => ({
            label: kommune,
            value: `kommune-${slugifyValue(kommune)}`,
        })),
    }));

    const standaloneOptions = Array.from(standaloneMunicipalities).map((kommune) => ({
        label: kommune,
        value: `kommune-${slugifyValue(kommune)}`,
    }));

    return sortConfigAlphabetically({
        groups,
        standaloneOptions,
    });
}

function buildBackendSectorConfig(rows: BackendProsjektRow[]): GroupedSuggestionConfig {
    const sectorValues = Array.from(
        new Set(rows.map((row) => row.sektor?.trim()).filter((value): value is string => Boolean(value))),
    );

    return {
        groups: [],
        standaloneOptions: sectorValues.map((value) => ({
            label: value,
            value: slugifyValue(value),
        })),
    };
}

function buildBackendStotteobjektConfig(rows: BackendProsjektRow[]): GroupedSuggestionConfig {
    const underkategorierByHovedkategori = new Map<string, Set<string>>();

    rows.forEach((row) => {
        const hovedkategori = row.stotteobjekt_navn?.trim();
        const underkategori = row.stotteobjekt_underkategori?.trim();

        if (!hovedkategori || hovedkategori === "Ukjent") {
            return;
        }

        const underkategorier = underkategorierByHovedkategori.get(hovedkategori) ?? new Set<string>();

        if (underkategori && underkategori !== "Ukjent" && underkategori !== hovedkategori) {
            underkategorier.add(underkategori);
        }

        underkategorierByHovedkategori.set(hovedkategori, underkategorier);
    });

    const groups = Array.from(underkategorierByHovedkategori.entries()).map(([hovedkategori, underkategorier]) => ({
        label: hovedkategori,
        value: hovedkategori,
        children: Array.from(underkategorier).map((underkategori) => ({
            label: underkategori,
            // Child values must be unique per parent to avoid cross-group selection collisions.
            value: `${hovedkategori}::${underkategori}`,
        })),
    }));

    return sortConfigAlphabetically({
        groups,
    });
}

const backendRegionSuggestionConfig = buildBackendRegionConfig(backendProsjekterRaw as BackendProsjektRow[]);
const backendSectorSuggestionConfig = buildBackendSectorConfig(backendProsjekterRaw as BackendProsjektRow[]);
const backendStotteobjektSuggestionConfig = buildBackendStotteobjektConfig(
    backendProsjekterRaw as BackendProsjektRow[],
);
const mockRegionSuggestionConfig = buildBackendRegionConfig(mockProsjekter as BackendProsjektRow[]);
const mockSectorSuggestionConfig = buildBackendSectorConfig(mockProsjekter as BackendProsjektRow[]);
const mockStotteobjektSuggestionConfig = buildBackendStotteobjektConfig(mockProsjekter as BackendProsjektRow[]);

function getInitialDataSourceFromUrl(): ProsjektlisteDataSource | null {
    if (typeof window === "undefined") {
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const source = params.get(prosjektlisteDataSourceParamKey);

    if (source === "mock" || source === "backend") {
        return source;
    }

    return null;
}

export function getConfiguredDataSource(): ProsjektlisteDataSource {
    const fromUrl = getInitialDataSourceFromUrl();
    if (fromUrl) {
        return fromUrl;
    }

    const fromEnv = import.meta.env.VITE_PROSJEKTLISTE_DATA_SOURCE;
    return fromEnv === "mock" ? "mock" : "backend";
}

export function getProsjekterForDataSource(dataSource: ProsjektlisteDataSource): ProsjektRow[] {
    return dataSource === "backend" ? backendProsjekter : mockProsjekterMapped;
}

export function getGroupedFilterConfigForDataSource(
    dataSource: ProsjektlisteDataSource,
    key: FilterKey,
): GroupedSuggestionConfig | undefined {
    if (dataSource === "mock") {
        if (key === "Region") {
            return mockRegionSuggestionConfig;
        }

        if (key === "Sektor") {
            return mockSectorSuggestionConfig;
        }

        if (key === "Støtteobjekt") {
            return mockStotteobjektSuggestionConfig;
        }

        return undefined;
    }

    if (key === "Region") {
        return backendRegionSuggestionConfig;
    }

    if (key === "Sektor") {
        return backendSectorSuggestionConfig;
    }

    if (key === "Støtteobjekt") {
        return backendStotteobjektSuggestionConfig;
    }

    return undefined;
}
